/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Typography
} from '@one-thd/sui-atomic-components';
import proptypes from 'prop-types';
import { useFitCompatibility } from '../FitCompatibilityProvider';
import {
  Text,
  Tip,
  List,
  Image,
  TabContent
} from './drawerComponents';
import { REFRIGERATOR_MAPPING } from '../../applianceConstants/refrigerator';
import { LAUNDRY_DRAWER_MAPPING } from '../../applianceConstants/laundry';
import { RANGE_DRAWER_MAPPING } from '../../applianceConstants/range';
import { track } from '../../utils';

const { string } = proptypes;

export const FitDrawer = () => {
  const {
    drawerPosition,
    hasInitialBackArrow,
    drawerOpen,
    setDrawerOpen,
    drawerIndex,
    setDrawerIndex,
    applianceType,
  } = useFitCompatibility();

  const [selectedTab, setSelectedTab] = useState(0);

  const onDrawerClose = () => {
    setDrawerOpen(false);
    setDrawerIndex(0);
    setSelectedTab(0);
  };

  let mapping;
  switch (applianceType) {
  case 'REFRIGERATOR':
    mapping = REFRIGERATOR_MAPPING;
    break;
  case 'RANGE':
    mapping = RANGE_DRAWER_MAPPING;
    break;
  default:
    mapping = LAUNDRY_DRAWER_MAPPING;
    break;
  }

  const { header, sections, footer } = mapping[drawerIndex];
  const correctMapping = mapping;

  const buildSectionClassnames = ({
    subIndex,
    flexGroup = false,
    hasBorder = false,
    shiftLeft = false,
    marginOverride = null,
    marginTopOverride = null,
  }) => {
    return classnames({
      'sui-mb-4': subIndex === 0,
      'sui-mb-2': subIndex === 1,
      'sui-mb-1': subIndex >= 2,
      'sui-ml-6': shiftLeft,
      'sui-flex sui-justify-center sui-gap-4': flexGroup,
      'sui-border-solid sui-border-1 sui-border-primary sui-rounded-base': hasBorder,
      '!sui-mb-0': marginOverride === 0,
      '!sui-mb-1': marginOverride === 1,
      '!sui-mb-2': marginOverride === 2,
      '!sui-mb-3': marginOverride === 3,
      '!sui-mb-4': marginOverride === 4,
      '!sui-mb-5': marginOverride === 5,
      '!sui-mb-6': marginOverride === 6,
      '!sui-mb-8': marginOverride === 8,
      '!sui-mt-2': marginTopOverride === 2,
    });
  };

  const buildDrawer = (drawerSections, subIndex = 0) => {
    return drawerSections.map((section) => {
      if (Array.isArray(section)) return buildDrawer(section, subIndex + 1);
      const [[type, content], [_, modifiers = {}] = []] = Object.entries(section);
      const {
        flexGroup,
        hasBorder,
        shiftLeft,
        marginOverride,
        marginTopOverride,
        sideBySide,
        stacked,
        ranges,
        centered,
        separator
      } = modifiers;

      const sectionClassnames = buildSectionClassnames({
        subIndex,
        flexGroup,
        hasBorder,
        shiftLeft,
        marginOverride,
        marginTopOverride,
        sideBySide,
        stacked,
        ranges,
        centered,
        separator
      });

      switch (type) {
      case 'text': {
        const {
          id, string: textString, isTitle, isTiny, boldString, linkString, linkUrl
        } = content;
        return (
          <div key={id} className={sectionClassnames}>
            <Text
              text={textString}
              isTitle={isTitle}
              isTiny={isTiny}
              boldText={boldString}
              linkText={linkString}
              linkUrl={linkUrl}
            />
          </div>
        );
      }
      case 'list': {
        const { id, items } = content;
        return (
          <div key={id} className={sectionClassnames}>
            <List list={items} />
          </div>
        );
      }
      case 'image': {
        const {
          id,
          src,
          alt,
          size,
          subHeader,
          subSubHeader
        } = content;
        return (
          <div key={id} className={sectionClassnames}>
            <Image
              src={src}
              alt={alt}
              size={size}
              subHeader={subHeader}
              subSubHeader={subSubHeader}
              sideBySide={sideBySide}
              stacked={stacked}
              ranges={ranges}
              centered={centered}
              separator={separator}
            />
          </div>
        );
      }
      case 'tip': {
        const { id, strings: tipStrings = [], sections: tipSections = [], } = content;
        return (
          <div
            key={id}
            className={sectionClassnames}
          >
            <Tip texts={tipStrings}>
              <>{tipSections.length ? buildDrawer(tipSections, subIndex + 1) : null}</>
            </Tip>
          </div>
        );
      }
      case 'sections': {
        const { id, items } = content;
        return (
          <div key={id} className={sectionClassnames}>
            {items.map((item, index) => (
              <React.Fragment key={item.id || index}>
                {buildDrawer([item], subIndex + 1)}
              </React.Fragment>
            ))}
          </div>
        );
      }
      case 'imageSeparator': {
        const { id, items, separator: separatorString } = content;
        const wrapperClassnames = 'sui-flex sui-items-stretch sui-justify-center sui-gap-8 sui-mb-6 sui-max-w-[75%]';
        const imageClassnames = 'sui-flex sui-flex-col sui-gap-4 sui-items-center sui-min-w-24 sui-justify-end';
        const isOrSeparator = separatorString === 'or';
        const separatorClassnames = `sui-m-auto sui-pb-6 ${isOrSeparator ? 'sui-text-3xl' : 'sui-text-6xl'}`;
        return (
          <div key={id} className="sui-flex sui-justify-center">
            <div className={wrapperClassnames}>
              {items.map((item, index) => {
                const showSeparator = !!separatorString && index < items.length - 1;
                const { image } = item;
                const {
                  id: imageId, src, alt, height, width, subHeader
                } = image;

                return (
                  <React.Fragment key={imageId}>
                    <div className={imageClassnames}>
                      <img
                        key={`image - ${src}`}
                        src={src}
                        alt={`Image: ${alt}`}
                        height={height}
                        width={width}
                      />
                      {subHeader && (
                        <div key={`${subHeader} - ${src}`} className="sui-text-nowrap">
                          <Typography
                            variant="body-xs"
                            component="span"
                          >
                            {subHeader}
                          </Typography>
                        </div>
                      )}
                    </div>
                    {showSeparator && (
                      <div key={`separator - ${src}`} className="sui-flex sui-items-center">
                        <span className={separatorClassnames}>
                          {separatorString === 'plus' ? '+' : 'or'}
                        </span>
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      }
      case 'tabs': {
        const { id, items } = content;
        return (
          <div key={id} className={sectionClassnames}>
            <TabContent
              id={id}
              items={items}
              buildDrawer={buildDrawer}
              selectedTab={selectedTab}
              onTabChange={(tabId, newValue) => { setSelectedTab(newValue); }}
            />
          </div>
        );
      }
      default: {
        return null;
      }
      }
    });
  };

  const scrollToTop = () => {
    if (typeof document === 'undefined') return;

    const drawerElement = document.getElementById('fit-compatibility-drawer');
    if (drawerElement) {
      drawerElement.scrollTop = 0;
    }
  };

  if (drawerIndex === 4) {
    track('step4');
  }

  return (
    <Drawer
      DrawerRootProps={{
        id: 'fit-compatibility-drawer'
      }}
      position={drawerPosition}
      open={drawerOpen}
      onClose={onDrawerClose}
    >
      <DrawerHeader
        title={header}
        onClose={onDrawerClose}
        onBack={
          drawerIndex === 0 && !hasInitialBackArrow
            ? null
            : () => {
              if (drawerIndex === 0 && hasInitialBackArrow) {
                onDrawerClose();
              } else {
                setDrawerIndex(drawerIndex - 1);
              }
              scrollToTop();
            }
        }
      />
      <DrawerBody>
        <div className="sui-mt-2">
          {buildDrawer(sections)}
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button
          fullWidth
          onClick={() => {
            const indexBump = drawerIndex + 1;
            if (correctMapping[indexBump] === undefined) {
              onDrawerClose();
            } else {
              scrollToTop();
              setDrawerIndex(indexBump);
            }
          }}
        >
          {footer}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
